import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  accountTicketGroupsQuery,
  addAccountTicketGroup,
  removeAccountTicketGroup,
  type AccountTicketGroups as Page,
} from '../../api/settingsUsers'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { RaSection } from './AccountRoles'
import { failed } from './shared'

/**
 * Grupurile de tichete ale contului si ce tichete vede omul (cerinta CEC,
 * 16 octombrie 2026).
 *
 * Grupurile de tichete sunt departamentele pe care se atribuie tichetele. Omul
 * e in departamentul lui (campul "Group" din detalii) si, peste el, in cele de
 * aici. Cele venite dintr-un grup din director (IAM / SCIM) nu se scot de aici:
 * ar reveni la urmatoarea sincronizare.
 *
 * Ce tichete vede vine din ROLURI (Roles and Resources): cea mai larga treapta
 * dintre rolul principal si cele suplimentare. Aici doar se arata.
 */
export function AccountTicketGroups({ userId }: { userId: number }) {
  const client = useQueryClient()
  const page = useQuery(accountTicketGroupsQuery(userId))
  const [chosen, setChosen] = useState('')
  const [busy, setBusy] = useState(false)

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const data = page.data

  const run = (action: Promise<unknown>, done: string) => {
    setBusy(true)
    void action
      .then(() => {
        toast(done, 'Success')
        setChosen('')
        void client.invalidateQueries({ queryKey: ['settings', 'users', 'accounts', userId, 'ticket-groups'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <RaSection title="Ticket visibility" className="account-ticket-groups">
      <div className="od-ra-block">
        <TicketVisibility effective={data.effective} />
      </div>

      <div className="od-ra-block">
        <span className="od-ra-sub">Ticket groups</span>
        <p className="od-ra-lead">
          The groups this person works on. They count when a role shows only the tickets of its own groups.
        </p>

        <ul className="od-ra-chips" data-testid="ticket-groups">
          {data.primary ? (
            <li className="od-ra-chip">
              <span>{data.primary.name}</span>
              <span className="od-ra-chip-note" title="The department of the account, set in Edit user">
                department
              </span>
            </li>
          ) : null}
          {data.groups.map((group) => {
            const removable = data.mayEdit && !group.fromDirectory
            return (
              <li key={group.id} className={removable ? 'od-ra-chip od-ra-chip-removable' : 'od-ra-chip'}>
                <span>{group.name}</span>
                {group.fromDirectory ? (
                  <span className="od-ra-chip-note" data-testid="ticket-group-from-directory">
                    from a directory group
                  </span>
                ) : null}
                {removable ? (
                  <button
                    type="button"
                    className="od-ra-chip-x"
                    disabled={busy}
                    aria-label={`Remove ticket group ${group.name}`}
                    title="Remove"
                    onClick={() => run(removeAccountTicketGroup(userId, group.id), 'Ticket group removed')}
                  >
                    &times;
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
        {!data.primary && data.groups.length === 0 ? (
          <p className="od-ra-empty" data-testid="no-ticket-groups">
            No ticket groups.
          </p>
        ) : null}

        {data.mayEdit ? (
          <div className="od-ra-add">
            <select
              className="form-control"
              aria-label="Add a ticket group"
              value={chosen}
              onChange={(event) => setChosen(event.target.value)}
            >
              <option value="">Add a ticket group...</option>
              {data.available.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || chosen === ''}
              onClick={() => run(addAccountTicketGroup(userId, Number(chosen)), 'Ticket group added')}
            >
              Add group
            </button>
          </div>
        ) : null}
      </div>
    </RaSection>
  )
}

/** Ce tichete vede omul, citit din rolurile lui - numai de citit. */
function TicketVisibility({ effective }: { effective: Page['effective'] }) {
  return (
    <div data-testid="ticket-visibility">
      <div className="od-ra-scope">
        <span className="od-ra-sub">Tickets visible</span>
        <span className="od-ra-pill" data-testid="ticket-visibility-label">
          {effective.label}
        </span>
      </div>
      <p className="od-ra-hint">
        {effective.admin
          ? 'An administrator sees every ticket.'
          : effective.roles.length === 0
            ? 'The account has no role, so it sees every ticket it saw before.'
            : `From roles: ${effective.roles.map((role) => `${role.name} (${role.label})`).join(', ')}. The widest one applies; change it in Roles and Resources.`}
      </p>
    </div>
  )
}
